import React, { useEffect, useState, useRef } from "react";
import { Navigate, Link } from "react-router-dom";
import { LogOut, FileUp, MapPin, Plus, Trash2, Image as ImageIcon, FileText, BarChart3, Folder, X, Save, Edit, ShieldCheck, Sparkles, Wand2, Layers, CheckCircle2, XCircle, Clock, Languages, Map as MapIcon, Settings, Route, Send, Download, Activity, Images, RotateCcw } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useSiteImagesReload } from "@/contexts/SiteImagesContext";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState("overview");

  if (loading) return <div className="font-admin p-10">Loading...</div>;
  if (!user) return <Navigate to="/admin" replace />;

  const tabs = [
    { id: "overview", label: t.admin.stats, icon: <BarChart3 className="w-4 h-4" /> },
    { id: "places", label: t.admin.places, icon: <MapPin className="w-4 h-4" /> },
    { id: "files", label: t.admin.files, icon: <Folder className="w-4 h-4" /> },
    { id: "ai", label: "AI Studio", icon: <Sparkles className="w-4 h-4" /> },
    { id: "itineraries", label: "Itineraries", icon: <Route className="w-4 h-4" /> },
    { id: "maps", label: "Tourist Maps", icon: <Layers className="w-4 h-4" /> },
    { id: "images", label: "Site Images", icon: <Images className="w-4 h-4" /> },
    { id: "settings", label: "AI Config", icon: <Settings className="w-4 h-4" /> },
  ];

  return (
    <div className="font-admin min-h-screen bg-muted/30">
      <header className="bg-white border-b border-border sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-[hsl(var(--primary-green))]" />
            <h1 className="font-semibold">{t.admin.dashboard}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">View site →</Link>
            <span className="text-sm text-muted-foreground hidden sm:inline">{user.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border hover:bg-muted text-sm"
            >
              <LogOut className="w-4 h-4" /> {t.admin.logout}
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex gap-1 overflow-x-auto">
          {tabs.map(tt => (
            <button
              key={tt.id}
              onClick={() => setTab(tt.id)}
              data-testid={`admin-tab-${tt.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                tab === tt.id ? "border-[hsl(var(--primary-red))] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tt.icon} {tt.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {tab === "overview" && <Overview />}
        {tab === "places" && <PlacesAdmin />}
        {tab === "files" && <FilesAdmin />}
        {tab === "ai" && <AIStudio onPlacesChanged={() => {}} />}
        {tab === "itineraries" && <ItinerariesAdmin />}
        {tab === "maps" && <TouristMapsAdmin />}
        {tab === "images" && <SiteImagesAdmin />}
        {tab === "settings" && <AISettingsTab />}
      </main>
    </div>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(r => setData(r.data)); }, []);
  if (!data) return <p className="text-muted-foreground">Loading...</p>;
  const s = data.stats;

  const cards = [
    { label: "Places", value: s.total_places, sub: `${s.published_places} published` },
    { label: "Files", value: s.total_files, sub: `${s.total_documents} docs · ${s.total_images} images` },
    { label: "Categories", value: s.total_categories },
    { label: "AI Conversations", value: s.ai_conversations },
  ];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => (
          <div key={c.label} className="p-5 bg-white border border-border rounded-md">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{c.label}</p>
            <p className="mt-2 text-3xl font-semibold tabular-nums">{c.value}</p>
            {c.sub && <p className="mt-1 text-xs text-muted-foreground">{c.sub}</p>}
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-border rounded-md p-5">
          <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Recent files</h3>
          <ul className="text-sm space-y-2">
            {data.recent_files.map(f => (
              <li key={f.id} className="flex items-center justify-between gap-3">
                <span className="truncate flex items-center gap-2">
                  {f.file_type === "image" ? <ImageIcon className="w-4 h-4 text-[hsl(var(--primary-green))]" /> : <FileText className="w-4 h-4 text-[hsl(var(--primary-red))]" />}
                  <span className="truncate">{f.title}</span>
                </span>
                <span className="text-xs text-muted-foreground flex-shrink-0">{f.extension}</span>
              </li>
            ))}
            {data.recent_files.length === 0 && <li className="text-muted-foreground text-xs">—</li>}
          </ul>
        </div>
        <div className="bg-white border border-border rounded-md p-5">
          <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Recent places</h3>
          <ul className="text-sm space-y-2">
            {data.recent_places.map(p => (
              <li key={p.id} className="flex items-center justify-between gap-3">
                <span className="truncate">{p.title?.es || p.title?.en || p.slug}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${p.status === "published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
              </li>
            ))}
            {data.recent_places.length === 0 && <li className="text-muted-foreground text-xs">—</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function PlacesAdmin() {
  const [places, setPlaces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = () => {
    api.get("/admin/places").then(r => setPlaces(r.data));
    api.get("/admin/categories").then(r => setCategories(r.data));
  };
  useEffect(() => { refresh(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/places/${id}`);
    refresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">{`${places.length} places`}</h2>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          data-testid="admin-add-place-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--primary-red))] text-white text-sm font-semibold hover:bg-[hsl(var(--primary-red-hover))]"
        >
          <Plus className="w-4 h-4" /> Add place
        </button>
      </div>

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left">
            <tr>
              <th className="px-4 py-2 font-medium">Title</th>
              <th className="px-4 py-2 font-medium">Category</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Featured</th>
              <th className="px-4 py-2 font-medium">Source</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody data-testid="admin-places-table">
            {places.map(p => {
              const cat = categories.find(c => c.id === p.category_id);
              return (
                <tr key={p.id} className="border-t border-border">
                  <td className="px-4 py-2.5 font-medium">{p.title?.es || p.title?.en || p.slug}</td>
                  <td className="px-4 py-2.5">{cat?.name?.es || cat?.name?.en || "—"}</td>
                  <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded ${p.status === "published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span></td>
                  <td className="px-4 py-2.5">{p.featured ? "✓" : ""}</td>
                  <td className="px-4 py-2.5">{p.ai_generated ? <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" />AI</span> : <span className="text-xs text-muted-foreground">manual</span>}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => { setEditing(p); setShowForm(true); }} data-testid={`admin-edit-place-${p.slug}`} className="p-1.5 rounded hover:bg-muted">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(p.id)} data-testid={`admin-delete-place-${p.slug}`} className="p-1.5 rounded hover:bg-red-50 text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {places.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No places</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <PlaceForm
          place={editing}
          categories={categories}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); refresh(); }}
        />
      )}
    </div>
  );
}

function PlaceForm({ place, categories, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    title_es: place?.title?.es || "",
    title_en: place?.title?.en || "",
    title_fr: place?.title?.fr || "",
    short_es: place?.short_description?.es || "",
    short_en: place?.short_description?.en || "",
    short_fr: place?.short_description?.fr || "",
    desc_es: place?.description?.es || "",
    desc_en: place?.description?.en || "",
    desc_fr: place?.description?.fr || "",
    category_id: place?.category_id || (categories[0]?.id || ""),
    address: place?.address || "",
    latitude: place?.latitude || 0,
    longitude: place?.longitude || 0,
    schedule: place?.schedule || "",
    price_range: place?.price_range || "",
    image_url: place?.image_url || "",
    featured: place?.featured || false,
    status: place?.status || "published",
  }));
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: { es: form.title_es, en: form.title_en, fr: form.title_fr },
      short_description: { es: form.short_es, en: form.short_en, fr: form.short_fr },
      description: { es: form.desc_es, en: form.desc_en, fr: form.desc_fr },
      category_id: form.category_id,
      address: form.address,
      latitude: parseFloat(form.latitude) || 0,
      longitude: parseFloat(form.longitude) || 0,
      schedule: form.schedule,
      price_range: form.price_range,
      image_url: form.image_url,
      featured: form.featured,
      status: form.status,
    };
    try {
      if (place) await api.put(`/admin/places/${place.id}`, payload);
      else await api.post("/admin/places", payload);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const aiCall = async (key, body) => {
    setAiBusy(key);
    try {
      const r = await api.post("/admin/ai/generate-text", body);
      return r.data.result;
    } catch (e) {
      alert(e?.response?.data?.detail || "AI error");
      return null;
    } finally {
      setAiBusy(null);
    }
  };

  const aiGenerateDesc = async () => {
    if (!form.title_es) { alert("Fill Title ES first"); return; }
    const cat = categories.find(c => c.id === form.category_id);
    const r = await aiCall("genDesc", { operation: "generate_description", title: form.title_es, category_hint: cat?.type || "" });
    if (r) setForm({ ...form, desc_es: r });
  };
  const aiTranslate = async (target) => {
    const sourceText = form.desc_es || form.short_es || form.title_es;
    if (!sourceText) { alert("Need ES text first"); return; }
    const r = await aiCall(`tr-${target}`, { operation: "translate", text: sourceText, target_lang: target });
    if (r) {
      if (form.desc_es) setForm({ ...form, [`desc_${target}`]: r });
      else setForm({ ...form, [`short_${target}`]: r });
    }
  };
  const aiImprove = async () => {
    if (!form.desc_es) { alert("ES description empty"); return; }
    const r = await aiCall("improve", { operation: "improve", text: form.desc_es });
    if (r) setForm({ ...form, desc_es: r });
  };
  const aiShortFromLong = async () => {
    if (!form.desc_es) { alert("ES description empty"); return; }
    const r = await aiCall("short", { operation: "short_summary", text: form.desc_es });
    if (r) setForm({ ...form, short_es: r });
  };

  const F = ({ label, children }) => (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground block mb-1">{label}</span>
      {children}
    </label>
  );
  const inp = "w-full px-3 py-2 border border-border rounded-md text-sm outline-none focus:ring-2 ring-[hsl(var(--primary-red))]";
  const aiBtn = "inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50 transition";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-place-form">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold">{place ? "Edit" : "New"} place</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          {/* AI bar */}
          <div className="flex flex-wrap gap-2 p-3 bg-purple-50/50 border border-purple-100 rounded-md">
            <span className="text-xs font-semibold text-purple-700 flex items-center gap-1 mr-1"><Wand2 className="w-3.5 h-3.5" /> AI tools:</span>
            <button type="button" onClick={aiGenerateDesc} disabled={aiBusy} data-testid="ai-gen-desc-btn" className={aiBtn}>
              <Sparkles className="w-3 h-3" /> {aiBusy === "genDesc" ? "..." : "Generate description (ES)"}
            </button>
            <button type="button" onClick={aiImprove} disabled={aiBusy} data-testid="ai-improve-btn" className={aiBtn}>
              <Sparkles className="w-3 h-3" /> {aiBusy === "improve" ? "..." : "Improve ES"}
            </button>
            <button type="button" onClick={aiShortFromLong} disabled={aiBusy} className={aiBtn}>
              <Sparkles className="w-3 h-3" /> {aiBusy === "short" ? "..." : "Short summary"}
            </button>
            <button type="button" onClick={() => aiTranslate("en")} disabled={aiBusy} data-testid="ai-translate-en-btn" className={aiBtn}>
              <Languages className="w-3 h-3" /> {aiBusy === "tr-en" ? "..." : "Translate → EN"}
            </button>
            <button type="button" onClick={() => aiTranslate("fr")} disabled={aiBusy} data-testid="ai-translate-fr-btn" className={aiBtn}>
              <Languages className="w-3 h-3" /> {aiBusy === "tr-fr" ? "..." : "Translate → FR"}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <F label="Title ES"><input className={inp} value={form.title_es} onChange={e => setForm({...form, title_es: e.target.value})} required data-testid="form-title-es" /></F>
            <F label="Title EN"><input className={inp} value={form.title_en} onChange={e => setForm({...form, title_en: e.target.value})} /></F>
            <F label="Title FR"><input className={inp} value={form.title_fr} onChange={e => setForm({...form, title_fr: e.target.value})} /></F>

            <F label="Short ES"><input className={inp} value={form.short_es} onChange={e => setForm({...form, short_es: e.target.value})} /></F>
            <F label="Short EN"><input className={inp} value={form.short_en} onChange={e => setForm({...form, short_en: e.target.value})} /></F>
            <F label="Short FR"><input className={inp} value={form.short_fr} onChange={e => setForm({...form, short_fr: e.target.value})} /></F>

            <div className="md:col-span-3"><F label="Description ES"><textarea className={inp} rows={3} value={form.desc_es} onChange={e => setForm({...form, desc_es: e.target.value})} data-testid="form-desc-es" /></F></div>
            <div className="md:col-span-3"><F label="Description EN"><textarea className={inp} rows={3} value={form.desc_en} onChange={e => setForm({...form, desc_en: e.target.value})} data-testid="form-desc-en" /></F></div>
            <div className="md:col-span-3"><F label="Description FR"><textarea className={inp} rows={3} value={form.desc_fr} onChange={e => setForm({...form, desc_fr: e.target.value})} data-testid="form-desc-fr" /></F></div>

            <F label="Category">
              <select className={inp} value={form.category_id} onChange={e => setForm({...form, category_id: e.target.value})} data-testid="form-category">
                {categories.map(c => <option key={c.id} value={c.id}>{c.name?.es || c.name?.en}</option>)}
              </select>
            </F>
            <F label="Status">
              <select className={inp} value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </F>
            <F label="Featured">
              <select className={inp} value={form.featured ? "1" : "0"} onChange={e => setForm({...form, featured: e.target.value === "1"})}>
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </F>

            <div className="md:col-span-3"><F label="Address"><input className={inp} value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></F></div>
            <F label="Latitude"><input type="number" step="any" className={inp} value={form.latitude} onChange={e => setForm({...form, latitude: e.target.value})} /></F>
            <F label="Longitude"><input type="number" step="any" className={inp} value={form.longitude} onChange={e => setForm({...form, longitude: e.target.value})} /></F>
            <F label="Schedule"><input className={inp} value={form.schedule} onChange={e => setForm({...form, schedule: e.target.value})} /></F>
            <F label="Price"><input className={inp} value={form.price_range} onChange={e => setForm({...form, price_range: e.target.value})} /></F>
            <div className="md:col-span-2"><F label="Image URL"><input className={inp} value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} /></F></div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="form-save-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--primary-red))] text-white text-sm font-semibold disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FilesAdmin() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [meta, setMeta] = useState({ title: "", description: "", language: "es" });
  const inputRef = useRef(null);

  const refresh = () => api.get("/admin/files").then(r => setFiles(r.data));
  useEffect(() => { refresh(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", meta.title || file.name);
      fd.append("description", meta.description);
      fd.append("language", meta.language);
      await api.post("/admin/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMeta({ title: "", description: "", language: "es" });
      refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/files/${id}`);
    refresh();
  };

  return (
    <div className="space-y-4">
      <div
        className={`p-8 border-2 border-dashed rounded-md text-center transition ${
          drag ? "border-[hsl(var(--primary-green))] bg-green-50" : "border-border bg-white"
        }`}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) upload(f); }}
        data-testid="file-drop-zone"
      >
        <FileUp className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
        <p className="font-semibold">Drag & drop a PDF or image</p>
        <p className="text-sm text-muted-foreground">or click to choose · Max 25MB · PDF/JPG/PNG/WEBP</p>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); }}
          data-testid="file-input"
        />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2 max-w-2xl mx-auto">
          <input className="px-3 py-2 border border-border rounded-md text-sm" placeholder="Title (optional)" value={meta.title} onChange={e => setMeta({...meta, title: e.target.value})} />
          <input className="px-3 py-2 border border-border rounded-md text-sm" placeholder="Description (optional)" value={meta.description} onChange={e => setMeta({...meta, description: e.target.value})} />
          <select className="px-3 py-2 border border-border rounded-md text-sm" value={meta.language} onChange={e => setMeta({...meta, language: e.target.value})}>
            <option value="es">ES</option><option value="en">EN</option><option value="fr">FR</option>
          </select>
        </div>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="file-choose-btn"
          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--primary-red))] text-white text-sm font-semibold disabled:opacity-50"
        >
          <FileUp className="w-4 h-4" /> {uploading ? "Uploading..." : "Choose file"}
        </button>
      </div>

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left">
            <tr>
              <th className="px-4 py-2 font-medium">File</th>
              <th className="px-4 py-2 font-medium">Type</th>
              <th className="px-4 py-2 font-medium">Lang</th>
              <th className="px-4 py-2 font-medium">Size</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody data-testid="admin-files-table">
            {files.map(f => (
              <tr key={f.id} className="border-t border-border">
                <td className="px-4 py-2.5">
                  <span className="flex items-center gap-2">
                    {f.file_type === "image" ? <ImageIcon className="w-4 h-4 text-[hsl(var(--primary-green))]" /> : <FileText className="w-4 h-4 text-[hsl(var(--primary-red))]" />}
                    <span className="truncate">{f.title}</span>
                  </span>
                </td>
                <td className="px-4 py-2.5 uppercase text-xs">{f.extension}</td>
                <td className="px-4 py-2.5 uppercase text-xs">{f.language}</td>
                <td className="px-4 py-2.5">{Math.round(f.size / 1024)} KB</td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex gap-1">
                    <a href={`${api.defaults.baseURL}/files/${f.id}/download`} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-muted text-[hsl(var(--primary-green))]" data-testid={`admin-file-view-${f.id}`}>
                      View
                    </a>
                    <button onClick={() => remove(f.id)} data-testid={`admin-file-delete-${f.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {files.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No files</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ AI Studio ============
function AIStudio() {
  const [pdfFiles, setPdfFiles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [processing, setProcessing] = useState(null);
  const [error, setError] = useState(null);

  const refresh = () => {
    api.get("/admin/files").then(r => setPdfFiles(r.data.filter(f => f.extension === "pdf")));
    api.get("/admin/ai/jobs").then(r => setJobs(r.data));
    api.get(`/admin/ai/suggestions?status_f=${statusFilter}`).then(r => setSuggestions(r.data));
  };
  useEffect(() => { refresh(); }, [statusFilter]);

  const process = async (fileId) => {
    setProcessing(fileId);
    setError(null);
    try {
      await api.post(`/admin/ai/process-document/${fileId}`);
      refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error");
    } finally {
      setProcessing(null);
    }
  };

  const approve = async (id) => {
    await api.post(`/admin/ai/suggestions/${id}/approve`, { edits: null });
    refresh();
  };
  const reject = async (id) => {
    await api.post(`/admin/ai/suggestions/${id}/reject`);
    refresh();
  };

  return (
    <div className="space-y-6" data-testid="admin-ai-studio">
      <div className="bg-white border border-border rounded-md p-5">
        <h2 className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-600" /> Process PDF documents with AI</h2>
        <p className="text-sm text-muted-foreground mt-1">Select a PDF previously uploaded in Files. AI will extract tourist places and propose drafts for review.</p>
        {error && <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded">{error}</p>}
        <div className="mt-4">
          {pdfFiles.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No PDFs uploaded. Go to Files tab to upload one first.</p>
          ) : (
            <ul className="space-y-2">
              {pdfFiles.map(f => (
                <li key={f.id} className="flex items-center justify-between gap-3 p-3 bg-muted/40 rounded-md">
                  <span className="flex items-center gap-2 truncate">
                    <FileText className="w-4 h-4 text-[hsl(var(--primary-red))]" />
                    <span className="truncate font-medium">{f.title}</span>
                    <span className="text-xs text-muted-foreground">{Math.round(f.size / 1024)} KB</span>
                  </span>
                  <button
                    onClick={() => process(f.id)}
                    disabled={!!processing}
                    data-testid={`ai-process-${f.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-purple-600 text-white text-xs font-semibold hover:bg-purple-700 disabled:opacity-50"
                  >
                    <Wand2 className="w-3.5 h-3.5" /> {processing === f.id ? "Processing..." : "Process with AI"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Jobs */}
      <div className="bg-white border border-border rounded-md p-5">
        <h2 className="font-semibold flex items-center gap-2"><Clock className="w-4 h-4" /> Recent AI jobs</h2>
        <div className="mt-3 space-y-2">
          {jobs.slice(0, 8).map(j => (
            <div key={j.id} className="flex items-center justify-between text-sm border-b border-border pb-2">
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{j.document_title || j.type}</div>
                {j.summary && <div className="text-xs text-muted-foreground truncate">{j.summary}</div>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{j.suggestions_count || 0} suggestions</span>
                <span className={`text-xs px-2 py-0.5 rounded ${j.status === "done" ? "bg-green-100 text-green-700" : j.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{j.status}</span>
              </div>
            </div>
          ))}
          {jobs.length === 0 && <p className="text-sm text-muted-foreground">No jobs yet</p>}
        </div>
      </div>

      {/* Suggestions */}
      <div className="bg-white border border-border rounded-md p-5" data-testid="ai-suggestions-panel">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[hsl(var(--primary-green))]" /> AI place suggestions</h2>
          <div className="flex gap-1">
            {["pending", "approved", "rejected", "all"].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`sug-filter-${s}`}
                className={`px-3 py-1 text-xs rounded-md ${statusFilter === s ? "bg-foreground text-white" : "bg-muted"}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-3">
          {suggestions.map(s => (
            <SuggestionCard key={s.id} sug={s} onApprove={() => approve(s.id)} onReject={() => reject(s.id)} />
          ))}
          {suggestions.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No {statusFilter} suggestions</p>}
        </div>
      </div>
    </div>
  );
}

function SuggestionCard({ sug, onApprove, onReject }) {
  const p = sug.payload || {};
  const isPending = sug.status === "pending";
  return (
    <div data-testid={`suggestion-${sug.id}`} className="border border-border rounded-md p-4 hover:border-purple-200 transition">
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold">{p.name}</h4>
            <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">{p.category_hint}</span>
            {typeof p.confidence === "number" && (
              <span className="text-xs text-muted-foreground">conf: {Math.round(p.confidence * 100)}%</span>
            )}
            <span className={`text-xs px-2 py-0.5 rounded ml-auto ${
              sug.status === "pending" ? "bg-amber-100 text-amber-700" :
              sug.status === "approved" ? "bg-green-100 text-green-700" :
              "bg-red-100 text-red-700"
            }`}>{sug.status}</span>
          </div>
          {p.short_description_es && <p className="text-sm mt-2">{p.short_description_es}</p>}
          {p.long_description_es && <p className="text-xs text-muted-foreground mt-2 line-clamp-3">{p.long_description_es}</p>}
          {p.address && <p className="text-xs text-muted-foreground mt-2">📍 {p.address}</p>}
          {p.tags && p.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {p.tags.map(t => <span key={t} className="text-xs px-2 py-0.5 bg-muted rounded-full">{t}</span>)}
            </div>
          )}
        </div>
      </div>
      {isPending && (
        <div className="mt-3 flex gap-2 justify-end">
          <button onClick={onReject} data-testid={`reject-${sug.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold border border-border hover:bg-red-50 text-red-600">
            <XCircle className="w-3.5 h-3.5" /> Reject
          </button>
          <button onClick={onApprove} data-testid={`approve-${sug.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-[hsl(var(--primary-green))] text-white hover:bg-[hsl(var(--primary-green-hover))]">
            <CheckCircle2 className="w-3.5 h-3.5" /> Approve & create draft
          </button>
        </div>
      )}
      {sug.status === "approved" && sug.approved_place_id && (
        <p className="mt-2 text-xs text-[hsl(var(--primary-green))]">✓ Place created (status: draft). Edit it in Places tab to add coordinates and publish.</p>
      )}
    </div>
  );
}

// ============ Tourist Maps ============
function TouristMapsAdmin() {
  const [maps, setMaps] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const refresh = () => api.get("/admin/maps").then(r => setMaps(r.data));
  useEffect(() => { refresh(); }, []);

  const togglePublish = async (m) => {
    await api.put(`/admin/maps/${m.id}`, { status: m.status === "published" ? "draft" : "published" });
    refresh();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/maps/${id}`);
    refresh();
  };

  return (
    <div className="space-y-4" data-testid="admin-tourist-maps">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">{`${maps.length} tourist maps`}</h2>
        <button
          onClick={() => setShowForm(true)}
          data-testid="admin-add-map-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--primary-red))] text-white text-sm font-semibold hover:bg-[hsl(var(--primary-red-hover))]"
        >
          <Plus className="w-4 h-4" /> Upload map
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {maps.map(m => (
          <div key={m.id} className="bg-white border border-border rounded-md overflow-hidden">
            <div className="aspect-[4/3] bg-muted relative">
              {m.map_type === "image" ? (
                <img src={fileUrl(m.url)} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[hsl(var(--primary-green))]/20 to-[hsl(var(--primary-red))]/10">
                  <MapIcon className="w-12 h-12 text-[hsl(var(--primary-green))]" />
                </div>
              )}
              <span className="absolute top-2 right-2 px-2 py-0.5 rounded bg-white text-xs font-semibold uppercase">{m.extension}</span>
              <span className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-semibold ${m.status === "published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{m.status}</span>
            </div>
            <div className="p-4">
              <p className="font-semibold truncate">{m.title?.es || m.title?.en}</p>
              <p className="text-xs text-muted-foreground truncate">{m.description?.es || ""}</p>
              <p className="text-xs text-muted-foreground mt-1">{m.language?.toUpperCase()} · {m.section}</p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => togglePublish(m)} data-testid={`map-toggle-${m.slug}`} className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold ${m.status === "published" ? "bg-amber-50 text-amber-700 hover:bg-amber-100" : "bg-[hsl(var(--primary-green))] text-white hover:bg-[hsl(var(--primary-green-hover))]"}`}>
                  {m.status === "published" ? "Unpublish" : "Publish"}
                </button>
                <a href={fileUrl(m.url)} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-md text-xs font-semibold border border-border hover:bg-muted">View</a>
                <button onClick={() => remove(m.id)} data-testid={`map-delete-${m.slug}`} className="px-2 py-1.5 rounded-md text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {maps.length === 0 && <p className="col-span-full text-center text-muted-foreground py-8">No maps uploaded</p>}
      </div>

      {showForm && <MapUploadForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); refresh(); }} />}
    </div>
  );
}

function MapUploadForm({ onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    title_es: "", title_en: "", title_fr: "",
    description_es: "", description_en: "", description_fr: "",
    language: "es", publish_type: "downloadable", section: "general",
  });
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { alert("Choose a file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await api.post("/admin/maps/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const inp = "w-full px-3 py-2 border border-border rounded-md text-sm";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-map-upload-form">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold">Upload tourist map</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">File (PDF / Image / GeoJSON)</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.geojson"
              onChange={e => setFile(e.target.files?.[0] || null)}
              required
              data-testid="map-file-input"
              className={inp}
            />
            {file && <p className="text-xs text-muted-foreground mt-1">{file.name} · {Math.round(file.size / 1024)} KB</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-muted-foreground block mb-1">Title ES *</label><input className={inp} value={form.title_es} onChange={e => setForm({...form, title_es: e.target.value})} required data-testid="map-title-es" /></div>
            <div><label className="text-xs font-medium text-muted-foreground block mb-1">Title EN</label><input className={inp} value={form.title_en} onChange={e => setForm({...form, title_en: e.target.value})} /></div>
            <div><label className="text-xs font-medium text-muted-foreground block mb-1">Title FR</label><input className={inp} value={form.title_fr} onChange={e => setForm({...form, title_fr: e.target.value})} /></div>
          </div>
          <div><label className="text-xs font-medium text-muted-foreground block mb-1">Description ES</label><textarea rows={2} className={inp} value={form.description_es} onChange={e => setForm({...form, description_es: e.target.value})} /></div>

          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-muted-foreground block mb-1">Language</label>
              <select className={inp} value={form.language} onChange={e => setForm({...form, language: e.target.value})}>
                <option value="es">ES</option><option value="en">EN</option><option value="fr">FR</option>
              </select>
            </div>
            <div><label className="text-xs font-medium text-muted-foreground block mb-1">Publish type</label>
              <select className={inp} value={form.publish_type} onChange={e => setForm({...form, publish_type: e.target.value})}>
                <option value="downloadable">Downloadable</option>
                <option value="viewer">Viewer</option>
                <option value="layer">Layer</option>
              </select>
            </div>
            <div><label className="text-xs font-medium text-muted-foreground block mb-1">Section</label>
              <select className={inp} value={form.section} onChange={e => setForm({...form, section: e.target.value})}>
                <option value="general">General</option>
                <option value="routes">Routes</option>
                <option value="museums">Museums</option>
                <option value="nightlife">Nightlife</option>
              </select>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm">Cancel</button>
          <button type="submit" disabled={uploading} data-testid="map-upload-submit" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--primary-red))] text-white text-sm font-semibold disabled:opacity-50">
            <Save className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============ AI Settings Tab ============
function AISettingsTab() {
  const [s, setS] = useState(null);
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [customKey, setCustomKey] = useState("");

  const refresh = () => {
    api.get("/admin/ai/settings").then(r => setS(r.data));
    api.get("/admin/ai/usage-logs?limit=20").then(r => setLogs(r.data));
  };
  useEffect(() => { refresh(); }, []);

  if (!s) return <p className="text-muted-foreground">Loading...</p>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        provider: s.provider,
        model: s.model,
        temperature: parseFloat(s.temperature),
        default_language: s.default_language,
        key_source: s.key_source,
        publication_mode: s.publication_mode,
        limits: s.limits,
      };
      if (customKey) body.custom_api_key = customKey;
      const r = await api.put("/admin/ai/settings", body);
      setS(r.data);
      setCustomKey("");
      alert("Saved");
    } finally {
      setSaving(false);
    }
  };

  const testConn = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/admin/ai/test-connection");
      setTestResult({ ok: true, ...r.data });
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Error" });
    } finally {
      setTesting(false);
      refresh();
    }
  };

  const inp = "w-full px-3 py-2 border border-border rounded-md text-sm";
  const lbl = "text-xs font-medium text-muted-foreground block mb-1";

  return (
    <div className="space-y-6" data-testid="ai-settings-tab">
      <form onSubmit={save} className="bg-white border border-border rounded-md p-6 space-y-4 max-w-3xl">
        <h2 className="font-semibold flex items-center gap-2"><Settings className="w-4 h-4" /> AI / ChatGPT Configuration</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className={lbl}>Provider</label>
            <select className={inp} value={s.provider} onChange={e => setS({...s, provider: e.target.value})}>
              <option value="openai">OpenAI</option>
            </select>
          </div>
          <div><label className={lbl}>Model</label>
            <select className={inp} value={s.model} onChange={e => setS({...s, model: e.target.value})} data-testid="ai-config-model">
              <option value="gpt-5.2">gpt-5.2 (latest)</option>
              <option value="gpt-5.1">gpt-5.1</option>
              <option value="gpt-5-mini">gpt-5-mini</option>
              <option value="gpt-4o">gpt-4o</option>
              <option value="gpt-4o-mini">gpt-4o-mini</option>
            </select>
          </div>
          <div><label className={lbl}>Temperature</label>
            <input type="number" step="0.1" min="0" max="2" className={inp} value={s.temperature} onChange={e => setS({...s, temperature: e.target.value})} />
          </div>
          <div><label className={lbl}>Default language</label>
            <select className={inp} value={s.default_language} onChange={e => setS({...s, default_language: e.target.value})}>
              <option value="es">ES</option><option value="en">EN</option><option value="fr">FR</option>
            </select>
          </div>
          <div><label className={lbl}>Publication mode</label>
            <select className={inp} value={s.publication_mode} onChange={e => setS({...s, publication_mode: e.target.value})}>
              <option value="manual">Manual</option>
              <option value="review">Review required</option>
              <option value="auto">Auto-publish</option>
            </select>
          </div>
          <div><label className={lbl}>Key source</label>
            <select className={inp} value={s.key_source} onChange={e => setS({...s, key_source: e.target.value})} data-testid="ai-config-key-source">
              <option value="emergent">Emergent LLM Key (default)</option>
              <option value="custom">Custom OpenAI key</option>
            </select>
          </div>
        </div>

        {s.key_source === "custom" && (
          <div>
            <label className={lbl}>Custom OpenAI API key (sk-...)</label>
            <input
              type="password"
              className={inp}
              placeholder={s.custom_api_key_masked || "sk-..."}
              value={customKey}
              onChange={e => setCustomKey(e.target.value)}
              data-testid="ai-config-custom-key"
            />
            <p className="text-xs text-muted-foreground mt-1">Stored encrypted server-side. Never sent to frontend after save. Currently: <code className="text-xs">{s.custom_api_key_masked || "(empty)"}</code></p>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4 pt-3 border-t border-border">
          <div><label className={lbl}>Daily cost (USD)</label>
            <input type="number" step="0.5" className={inp} value={s.limits?.daily_cost_usd || 0} onChange={e => setS({...s, limits: {...s.limits, daily_cost_usd: parseFloat(e.target.value)}})} />
          </div>
          <div><label className={lbl}>Requests/min</label>
            <input type="number" className={inp} value={s.limits?.requests_per_min || 0} onChange={e => setS({...s, limits: {...s.limits, requests_per_min: parseInt(e.target.value)}})} />
          </div>
          <div><label className={lbl}>Max tokens/req</label>
            <input type="number" className={inp} value={s.limits?.max_tokens_per_request || 0} onChange={e => setS({...s, limits: {...s.limits, max_tokens_per_request: parseInt(e.target.value)}})} />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-3 border-t border-border">
          <button type="submit" disabled={saving} data-testid="ai-config-save" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--primary-red))] text-white text-sm font-semibold disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "..." : "Save settings"}
          </button>
          <button type="button" onClick={testConn} disabled={testing} data-testid="ai-config-test" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--primary-green))] text-white text-sm font-semibold disabled:opacity-50">
            <Send className="w-4 h-4" /> {testing ? "Testing..." : "Test connection"}
          </button>
          {testResult && (
            <span className={`text-sm ${testResult.ok ? "text-[hsl(var(--primary-green))]" : "text-red-600"}`} data-testid="ai-config-test-result">
              {testResult.ok ? `✓ ${testResult.model} (${testResult.latency_ms}ms)` : `✗ ${testResult.error}`}
            </span>
          )}
        </div>
        {s.updated_at && <p className="text-xs text-muted-foreground">Last updated: {new Date(s.updated_at).toLocaleString()}</p>}
      </form>

      <div className="bg-white border border-border rounded-md p-6 max-w-3xl">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Activity className="w-4 h-4" /> Recent AI usage</h3>
        <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="ai-usage-logs">
          {logs.map(l => (
            <div key={l.id} className="flex items-center justify-between text-xs py-1.5 border-b border-border gap-3">
              <span className="font-mono text-muted-foreground flex-shrink-0">{new Date(l.created_at).toLocaleString()}</span>
              <span className="font-medium flex-1 truncate">{l.module}</span>
              <span className="text-muted-foreground">{l.model || "—"}</span>
              <span className={`px-2 py-0.5 rounded text-xs ${l.status === "ok" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{l.status}</span>
              <span className="text-muted-foreground tabular-nums">{l.latency_ms || "—"}ms</span>
            </div>
          ))}
          {logs.length === 0 && <p className="text-sm text-muted-foreground">No activity yet</p>}
        </div>
      </div>
    </div>
  );
}

// ============ Itineraries Tab ============
const PROFILES = [
  { id: "cultural", label: "Cultural" },
  { id: "family", label: "Family" },
  { id: "gastronomic", label: "Gastronomic" },
  { id: "historical", label: "Historical" },
  { id: "romantic", label: "Romantic" },
  { id: "adventure", label: "Adventure" },
  { id: "premium", label: "Premium" },
  { id: "nocturnal", label: "Nocturnal" },
  { id: "accessible", label: "Accessible" },
];
const ROUTE_TYPE_OPTS = [
  { id: "medio_dia", label: "Half day (~4h)" },
  { id: "dia_completo", label: "Full day (~8h)" },
  { id: "nocturna", label: "Night (~3h)" },
];

function ItinerariesAdmin() {
  const [items, setItems] = useState([]);
  const [places, setPlaces] = useState([]);
  const [showWizard, setShowWizard] = useState(false);

  const refresh = () => {
    api.get("/admin/itineraries").then(r => setItems(r.data));
    api.get("/admin/places").then(r => setPlaces(r.data.filter(p => p.status === "published")));
  };
  useEffect(() => { refresh(); }, []);

  const approve = async (id) => {
    await api.post(`/admin/itineraries/${id}/approve`);
    refresh();
  };
  const unpublish = async (id) => {
    await api.post(`/admin/itineraries/${id}/unpublish`);
    refresh();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/itineraries/${id}`);
    refresh();
  };
  const genPdf = async (id) => {
    await api.post(`/admin/itineraries/${id}/generate-pdf`);
    refresh();
  };

  return (
    <div className="space-y-4" data-testid="admin-itineraries">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">{`${items.length} itineraries`}</h2>
        <button
          onClick={() => setShowWizard(true)}
          data-testid="admin-new-itinerary-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--primary-red))] text-white text-sm font-semibold hover:bg-[hsl(var(--primary-red-hover))]"
        >
          <Sparkles className="w-4 h-4" /> Generate with AI
        </button>
      </div>

      <div className="space-y-3">
        {items.map(it => (
          <div key={it.id} data-testid={`itin-row-${it.slug}`} className="bg-white border border-border rounded-md p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold">{it.title}</h4>
                  <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">{it.profile}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-muted">{it.route_type}</span>
                  <span className="text-xs text-muted-foreground">{it.duration_minutes} min · {it.points?.length || 0} stops · {it.language?.toUpperCase()}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ml-auto ${it.status === "published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{it.status}</span>
                </div>
                {it.narrative_intro && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{it.narrative_intro}</p>}
                {it.pdf_file?.url && <a href={fileUrl(it.pdf_file.url)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-[hsl(var(--primary-green))] font-semibold"><Download className="w-3 h-3" /> PDF ({Math.round((it.pdf_file.size||0)/1024)} KB)</a>}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {it.status === "draft" ? (
                <button onClick={() => approve(it.id)} data-testid={`itin-approve-${it.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-[hsl(var(--primary-green))] text-white hover:bg-[hsl(var(--primary-green-hover))]">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve & publish
                </button>
              ) : (
                <button onClick={() => unpublish(it.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold border border-border hover:bg-muted">
                  Unpublish
                </button>
              )}
              <button onClick={() => genPdf(it.id)} data-testid={`itin-pdf-${it.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold border border-border hover:bg-muted">
                <FileText className="w-3.5 h-3.5" /> {it.pdf_file ? "Regen PDF" : "Generate PDF"}
              </button>
              <button onClick={() => remove(it.id)} data-testid={`itin-delete-${it.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs text-red-600 hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-muted-foreground py-8">No itineraries yet. Click "Generate with AI" to create one.</p>}
      </div>

      {showWizard && <ItineraryWizard places={places} onClose={() => setShowWizard(false)} onSaved={() => { setShowWizard(false); refresh(); }} />}
    </div>
  );
}

function ItineraryWizard({ places, onClose, onSaved }) {
  const [selected, setSelected] = useState(new Set());
  const [profile, setProfile] = useState("cultural");
  const [routeType, setRouteType] = useState("medio_dia");
  const [language, setLanguage] = useState("es");
  const [titleHint, setTitleHint] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (selected.size < 3) { setError("Select at least 3 places"); return; }
    if (selected.size > 10) { setError("Max 10 places"); return; }
    setError(null);
    setGenerating(true);
    try {
      await api.post("/admin/itineraries/generate", {
        place_ids: Array.from(selected),
        profile,
        route_type: routeType,
        language,
        title_hint: titleHint,
      });
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error");
    } finally {
      setGenerating(false);
    }
  };

  const inp = "w-full px-3 py-2 border border-border rounded-md text-sm";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="itinerary-wizard">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-600" /> Generate AI itinerary</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium block mb-1">Profile</label>
              <select className={inp} value={profile} onChange={e => setProfile(e.target.value)} data-testid="wiz-profile">
                {PROFILES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium block mb-1">Route type</label>
              <select className={inp} value={routeType} onChange={e => setRouteType(e.target.value)} data-testid="wiz-route-type">
                {ROUTE_TYPE_OPTS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium block mb-1">Language</label>
              <select className={inp} value={language} onChange={e => setLanguage(e.target.value)} data-testid="wiz-language">
                <option value="es">ES</option><option value="en">EN</option><option value="fr">FR</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium block mb-1">Title hint (optional)</label>
            <input className={inp} value={titleHint} onChange={e => setTitleHint(e.target.value)} placeholder="e.g. Hidden gems of the old town" />
          </div>

          <div>
            <label className="text-xs font-medium block mb-2">Select 3-10 places (selected: {selected.size})</label>
            <div className="border border-border rounded-md max-h-72 overflow-y-auto" data-testid="wiz-places-list">
              {places.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">No published places. Publish places with valid coordinates first.</p>
              ) : places.map(p => (
                <label key={p.id} className={`flex items-center gap-3 px-4 py-2.5 border-b border-border cursor-pointer hover:bg-muted/50 ${selected.has(p.id) ? "bg-purple-50" : ""}`}>
                  <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} data-testid={`wiz-place-${p.slug}`} />
                  <span className="flex-1 text-sm">{p.title?.es || p.title?.en || p.slug}</span>
                  <span className="text-xs text-muted-foreground">{p.address || "—"}</span>
                </label>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm">Cancel</button>
          <button type="submit" disabled={generating || selected.size < 3} data-testid="wiz-generate-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--primary-red))] text-white text-sm font-semibold disabled:opacity-50">
            <Sparkles className="w-4 h-4" /> {generating ? "Generating..." : "Generate"}
          </button>
        </div>
      </form>
    </div>
  );
}


// ============ Site Images Admin ============
function SiteImagesAdmin() {
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(null);
  const reloadPublic = useSiteImagesReload();

  const refresh = () => api.get("/admin/site-images").then(r => setSlots(r.data));
  useEffect(() => { refresh(); }, []);

  const upload = async (key, file) => {
    if (!file) return;
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/site-images/${key}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      reloadPublic();
    } catch (e) {
      alert(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(null);
    }
  };

  const reset = async (key) => {
    if (!window.confirm("Reset to default image?")) return;
    setBusy(key);
    try {
      await api.delete(`/admin/site-images/${key}`);
      await refresh();
      reloadPublic();
    } finally {
      setBusy(null);
    }
  };

  // Group by section
  const grouped = slots.reduce((acc, s) => {
    (acc[s.section] = acc[s.section] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="admin-site-images">
      <div className="bg-purple-50 border border-purple-200 rounded-md p-4">
        <p className="text-sm text-purple-900">
          <Images className="w-4 h-4 inline mr-1.5" /> Upload images here and they will replace the defaults shown on the public site (hero, category cards, fallbacks). Use <strong>landscape JPG/PNG/WEBP</strong>, ideally ≥1200×800px.
        </p>
      </div>

      {Object.entries(grouped).map(([section, items]) => (
        <div key={section}>
          <h2 className="text-lg font-semibold mb-3">{section}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(s => (
              <SiteImageSlot key={s.key} slot={s} busy={busy === s.key} onUpload={(f) => upload(s.key, f)} onReset={() => reset(s.key)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SiteImageSlot({ slot, busy, onUpload, onReset }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const previewUrl = slot.current_url ? (slot.current_url.startsWith("http") ? slot.current_url : `${api.defaults.baseURL.replace("/api", "")}${slot.current_url}`) : "";

  return (
    <div className="bg-white border border-border rounded-md overflow-hidden" data-testid={`site-image-slot-${slot.key}`}>
      <div
        className={`aspect-[16/10] bg-muted relative ${drag ? "ring-2 ring-[hsl(var(--primary-green))]" : ""}`}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) onUpload(f); }}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ImageIcon className="w-10 h-10" />
          </div>
        )}
        {slot.is_custom && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-[hsl(var(--primary-green))] text-white text-xs font-semibold">Custom</span>
        )}
        {!slot.current_url && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-semibold">Empty</span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-sm truncate">{slot.label}</h3>
        <p className="text-xs text-muted-foreground font-mono truncate">{slot.key}</p>
        <div className="mt-3 flex gap-2">
          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.gif"
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); if (e.target) e.target.value = ""; }}
            data-testid={`site-image-input-${slot.key}`}
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid={`site-image-upload-${slot.key}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-[hsl(var(--primary-red))] text-white text-xs font-semibold hover:bg-[hsl(var(--primary-red-hover))] disabled:opacity-50"
          >
            <FileUp className="w-3.5 h-3.5" /> {busy ? "..." : (slot.is_custom ? "Replace" : "Upload")}
          </button>
          {slot.is_custom && (
            <button
              onClick={onReset}
              disabled={busy}
              data-testid={`site-image-reset-${slot.key}`}
              className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold border border-border hover:bg-muted disabled:opacity-50"
              title="Reset to default"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

